import { AxiosError } from "axios";
import {
  ChartListResponse,
  ChartOrderBy,
  ChartRaceResponse,
  ChartResponse,
  ChartStatus,
  ChartUpdateRequest,
  CreateChartRequest,
  PagedChartList,
} from "../models/chart";
import { State } from "../models/state";
import { backend } from "../utils/axios";

const ROUTE = "/charts";

interface ChartsServiceEndpoints {
  createChart: (chart: CreateChartRequest) => Promise<ChartResponse>;
  getChart: (chartId: string) => Promise<ChartResponse>;
  getCharts: (
    status: ChartStatus,
    typeId: number,
    query?: string,
    sortdesc?: ChartOrderBy
  ) => Promise<ChartListResponse[]>;
  getChartsPaged: (
    status: ChartStatus,
    typeId: number,
    page: number,
    pageSize: number,
    query?: string,
    sortdesc?: ChartOrderBy
  ) => Promise<PagedChartList[]>;
  getChartsByType: (
    status: ChartStatus,
    typeId: number,
    query?: string,
    sortdesc?: ChartOrderBy,
    hasCompletedDeck?: boolean
  ) => Promise<ChartListResponse[]>;
  editChart: (chart: ChartUpdateRequest) => Promise<ChartResponse>;
  deleteChart: (chartId: string) => Promise<ChartResponse>;
  getRaceTypes: () => Promise<State[]>;
  getS3Link: (chartId: string, type: "download" | "print") => Promise<string>;
  getFullDeckS3Link: (chartId: string) => Promise<string>;
  getBallotFileS3Link: (chartId: string) => Promise<string>;
  adminCreateChart: (chart: CreateChartRequest) => Promise<ChartResponse>;
  adminEditChart: (chart: ChartUpdateRequest) => Promise<ChartResponse>;
  adminUploadPdf: (
    chartId: string,
    useSquareMarks: boolean,
    pdf: File,
    startPage?: string,
    endPage?: string
  ) => Promise<ChartResponse>;
  getChartDetailsForMarks: (chartId: string) => Promise<ChartResponse>;
  updateChartManualMarks: (chart: ChartUpdateRequest) => Promise<ChartResponse>;
  downloadAll: (
    chartIds: string[],
    fullDecks: boolean
  ) => Promise<{ data: any; filename: string }>;
  printAll: (
    chartIds: string[],
    fullDecks: boolean
  ) => Promise<{ data: any; filename: string }>;
  searchCharts: (
    chartId: string,
    query?: string,
    year?: string
  ) => Promise<ChartListResponse[]>;
  importMarksFromChart: (
    currentChartId: string,
    sourceChartId: string
  ) => Promise<ChartRaceResponse[]>;
  downloadPrintRequestedCharts: (
    chartIds: string[],
    fullDecks: boolean
  ) => Promise<{ data: any; filename: string }>;
  printPrintRequestedCharts: (
    chartIds: string[],
    fullDecks: boolean
  ) => Promise<{ data: any; filename: string }>;
  markPrintRequestedChartsAsPrinted: (
    chartIds: string[]
  ) => Promise<ChartResponse[]>;
  markChartsAsPrintRequested: (chartIds: string[]) => Promise<ChartResponse[]>;
  adminGetClerkChartsByStatus: (
    userId: string,
    status: ChartStatus,
    printed: boolean,
    printRequested: boolean,
    query?: string,
    electiondate?: string
  ) => Promise<ChartListResponse[]>;
}

export const ChartsService: ChartsServiceEndpoints = {
  async createChart(chart) {
    const res = await backend.post<ChartResponse>(ROUTE, chart);
    if (res.status !== 200) {
      const message = (res as unknown as AxiosError).response
        ?.data as unknown as string;
      throw new Error(message ?? "An error occurred while creating the chart.");
    }
    return res.data;
  },
  getChart: (chartId) =>
    backend
      .get<ChartResponse>(`${ROUTE}/details/${chartId}`)
      .then(({ data }) => data)
      .catch(),
  async getCharts(status, typeId, query, sortdesc) {
    const params = new URLSearchParams();
    if (query) params.append("query", query);
    if (sortdesc) params.append("sortdesc", sortdesc);

    const { data } = await backend.get<ChartListResponse[]>(
      `${ROUTE}/${status}/${typeId}`,
      { params }
    );

    return data;
  },
  async getChartsPaged(status, typeId, page, pageSize, query, sortdesc) {
    const params = new URLSearchParams();
    if (query) params.append("query", query);
    if (sortdesc) params.append("sortdesc", sortdesc);

    const { data } = await backend.get<PagedChartList[]>(
      `${ROUTE}/${status}/${typeId}/${page}/${pageSize}`,
      { params }
    );

    return data;
  },
  async getChartsByType(status, typeId, query, sortdesc, hasCompletedDeck) {
    const params = new URLSearchParams();
    if (query) params.append("query", query);
    if (sortdesc) params.append("sortdesc", sortdesc);
    if (typeof hasCompletedDeck === "boolean")
      params.append("hasCompletedDeck", String(hasCompletedDeck));

    const { data } = await backend.get<ChartListResponse[]>(
      `${ROUTE}/admin/${status}/${typeId}`,
      { params }
    );

    return data;
  },
  async editChart(chart) {
    const res = await backend.put<ChartResponse>(ROUTE, chart);
    if (res.status !== 200) {
      const message = (res as unknown as AxiosError).response
        ?.data as unknown as string;
      throw new Error(message ?? "An error occurred while editing the chart.");
    }
    return res.data;
  },
  deleteChart: (chartId) =>
    backend
      .delete<ChartResponse>(`${ROUTE}/${chartId}`)
      .then(({ data }) => data)
      .catch(),
  getRaceTypes: () =>
    backend
      .get<State[]>("/race-types")
      .then(({ data }) => data)
      .catch(),
  getS3Link: (chartId, type) =>
    backend
      .get<string>(`${ROUTE}/s3link/${chartId}/${type}`)
      .then(({ data }) => data)
      .catch(),
  getFullDeckS3Link: (chartId) =>
    backend
      .get<string>(`${ROUTE}/full-deck-s3link/${chartId}`)
      .then(({ data }) => data)
      .catch(),
  getBallotFileS3Link: (chartId) =>
    backend
      .get<string>(`${ROUTE}/ballot-s3link/${chartId}`)
      .then(({ data }) => data)
      .catch(),
  async adminCreateChart(chart) {
    const res = await backend.post<ChartResponse>(`${ROUTE}/admin`, chart);
    if (res.status !== 200) {
      const message = (res as unknown as AxiosError).response
        ?.data as unknown as string;
      throw new Error(message ?? "An error occurred while creating the chart.");
    }
    return res.data;
  },
  async adminEditChart(chart) {
    const res = await backend.put<ChartResponse>(`${ROUTE}/admin`, chart);
    if (res.status !== 200) {
      const message = (res as unknown as AxiosError).response
        ?.data as unknown as string;
      throw new Error(message ?? "An error occurred while updating the chart.");
    }
    return res.data;
  },
  async adminUploadPdf(chartId, useSquareMarks, pdf, startPage, endPage) {
    const params = new URLSearchParams();
    if (startPage) params.append("startPage", startPage);
    if (endPage) params.append("endPage", endPage);
    var formData = new FormData();
    formData.append(`files`, pdf);
    const { data } = await backend.post<ChartResponse>(
      `${ROUTE}/attach-ballot-pdf/${chartId}/${useSquareMarks}`,
      formData,
      { params }
    );
    return data;
  },
  getChartDetailsForMarks: (chartId) =>
    backend
      .get<ChartResponse>(`${ROUTE}/for-marks/${chartId}`)
      .then(({ data }) => data)
      .catch(),
  updateChartManualMarks: (chart) =>
    backend
      .put<ChartResponse>(`${ROUTE}/for-marks/${chart.id}`, chart)
      .then(({ data }) => data)
      .catch(),
  downloadAll: (chartIds, fullDecks) =>
    backend
      .post(
        `${ROUTE}/download`,
        { chartIds, fullDecks },
        { responseType: "blob" }
      )
      .then(({ data, headers }) => {
        return {
          data,
          filename: headers["file-name"] || "charts.zip",
        };
      })
      .catch(),
  printAll: (chartIds, fullDecks) =>
    backend
      .post(
        `${ROUTE}/download/pdf`,
        { chartIds, fullDecks },
        { responseType: "blob" }
      )
      .then(({ data, headers }) => {
        return {
          data,
          filename: headers["file-name"] || "charts.pdf",
        };
      })
      .catch(),
  searchCharts: (chartId, query, year) =>
    backend
      .get<ChartListResponse[]>(`${ROUTE}/import-search/${chartId}`, {
        params: {
          query,
          year,
        },
      })
      .then(({ data }) => data)
      .catch(),
  importMarksFromChart: (currentChartId, sourceChartId) =>
    backend
      .get<ChartRaceResponse[]>(
        `${ROUTE}/import-marks-for/${currentChartId}/from/${sourceChartId}`
      )
      .then(({ data }) => data)
      .catch(),
  downloadPrintRequestedCharts: (chartIds, fullDecks) =>
    backend
      .post(`${ROUTE}/print`, { chartIds, fullDecks }, { responseType: "blob" })
      .then(({ data, headers }) => {
        return {
          data,
          filename: headers["file-name"] || "charts.zip",
        };
      })
      .catch(),
  printPrintRequestedCharts: (chartIds, fullDecks) =>
    backend
      .post(
        `${ROUTE}/print/pdf`,
        { chartIds, fullDecks },
        { responseType: "blob" }
      )
      .then(({ data, headers }) => {
        return {
          data,
          filename: headers["file-name"] || "charts.pdf",
        };
      })
      .catch(),
  markPrintRequestedChartsAsPrinted: (chartIds) =>
    backend
      .post(`${ROUTE}/mark-printed`, { chartIds })
      .then(({ data }) => data)
      .catch(),
  markChartsAsPrintRequested: (chartIds) =>
    backend
      .post(`${ROUTE}/request-print`, { chartIds })
      .then(({ data }) => data)
      .catch(),
  adminGetClerkChartsByStatus: (
    userId,
    status,
    printed,
    printRequested,
    query,
    electiondate
  ) => {
    /*const params = new URLSearchParams();
    if (query) params.append("query", query);
    if (electiondate) params.append("electiondate", electiondate);*/

    return backend
      .get<ChartListResponse[]>(`${ROUTE}/admin/for-user/${userId}/${status}`, {
        params: {
          printed,
          printRequested,
          query,
          electiondate,
        },
      })
      .then(({ data }) => data)
      .catch();
  },
};
